import { useEffect } from 'react';

import { LAUNCH_READY, TrackingCategory } from '@/entities/tracking/const';
import { BootstrapSignalKeys, BootstrapSignals } from '@/entities/tracking/lib/bootstrap-signals';
import { TrackerEngine } from '@/entities/tracking/lib/tracker-engine';
import { getNavigationSequence } from '@/hooks/events/useTrackNavigation';
import { appLogger } from '@/utils/app-logger/appLogger';

const COMPONENT = 'useTrackLaunchReady';

// readDataLayerPageUpdate is injected by a Tealium extension that loads independently of
// dataHub/LAUNCH_READY; poll briefly instead of checking once so a slow-loading extension
// doesn't cause the page view to be silently dropped.
const READ_DATA_LAYER_POLL_INTERVAL_MS = 100;
const READ_DATA_LAYER_POLL_TIMEOUT_MS = 10_000;

// The shared data layer array carries both page-view pushes (flat objects with a `pageInfo`
// field) and interaction/event pushes (a different shape) — grabbing the literal last entry can
// capture an interaction that landed after this page's own page-view push. Search backward for
// the most recent page-view-shaped entry specifically.
const findLatestPageViewEntry = (dataLayer: unknown): unknown => {
    if (!Array.isArray(dataLayer)) return undefined;
    for (let i = dataLayer.length - 1; i >= 0; i -= 1) {
        const entry = dataLayer[i] as { pageInfo?: unknown } | undefined;
        if (entry && typeof entry === 'object' && entry.pageInfo !== undefined) return entry;
    }
    return undefined;
};

// Retries once on a transient throw (warn), then gives up and logs as error — bounded so
// this never loops.
const callReadDataLayerPageUpdateWithRetry = (isCurrentNavigation: () => boolean): void => {
    // Marked only on success and within the navigation that started the poll, so a stale
    // poll can't release the destination page's XHR-beacon waiters.
    const emit = () => {
        window.readDataLayerPageUpdate?.();
        if (isCurrentNavigation())
            BootstrapSignals.mark(BootstrapSignalKeys.PAGE_VIEW_BEACON_FIRED);
    };
    try {
        emit();
    } catch (firstError) {
        appLogger.warn('readDataLayerPageUpdate threw; retrying once', {
            component: COMPONENT,
            errorCode: firstError instanceof Error ? firstError.name : undefined,
        });
        try {
            emit();
        } catch (secondError) {
            appLogger.error(
                'readDataLayerPageUpdate threw again on retry; giving up',
                { component: COMPONENT },
                secondError,
            );
        }
    }
};

/**
 * Best-effort: readDataLayerPageUpdate is injected by Tealium and may arrive late — polls for
 * it, replaying this page's snapshot before a delayed call and restoring current state after.
 */
const signalPageViewToDataLayer = (): (() => void) => {
    if (typeof window === 'undefined') return () => {};

    const pageSnapshot = findLatestPageViewEntry(window.adobeDataLayer);

    let stopped = false;
    let timeoutId: number | undefined;
    let polledForReadDataLayerPageUpdate = false;
    const startedAt = Date.now();
    const startSequence = getNavigationSequence();
    const isCurrentNavigation = () => getNavigationSequence() === startSequence;
    const attempt = () => {
        if (stopped) return;
        if (typeof window.readDataLayerPageUpdate === 'function') {
            if (polledForReadDataLayerPageUpdate) {
                const waitedMs = Date.now() - startedAt;
                appLogger.info('readDataLayerPageUpdate became available after wait', {
                    component: COMPONENT,
                    waitedMs,
                });
                if (pageSnapshot !== undefined && Array.isArray(window.adobeDataLayer)) {
                    // Capture whatever is actually current (e.g. a page navigated to while this
                    // poll was in flight) before replaying this page's snapshot, so it can be
                    // restored below and the canonical state doesn't end up stuck on this page.
                    const currentSnapshot = findLatestPageViewEntry(window.adobeDataLayer);
                    window.adobeDataLayer.push(pageSnapshot);
                    callReadDataLayerPageUpdateWithRetry(isCurrentNavigation);
                    if (
                        currentSnapshot !== undefined &&
                        currentSnapshot !== pageSnapshot &&
                        Array.isArray(window.adobeDataLayer)
                    ) {
                        window.adobeDataLayer.push(currentSnapshot);
                    }
                    return;
                }
            }
            callReadDataLayerPageUpdateWithRetry(isCurrentNavigation);
            return;
        }
        polledForReadDataLayerPageUpdate = true;
        if (Date.now() - startedAt >= READ_DATA_LAYER_POLL_TIMEOUT_MS) {
            appLogger.warn(
                'readDataLayerPageUpdate never became available; page view not signaled',
                {
                    component: COMPONENT,
                    waitedMs: READ_DATA_LAYER_POLL_TIMEOUT_MS,
                },
            );
            return;
        }
        timeoutId = window.setTimeout(attempt, READ_DATA_LAYER_POLL_INTERVAL_MS);
    };
    attempt();

    return () => {
        stopped = true;
        if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
};

export function useTrackLaunchReady(): void {
    useEffect(() => {
        if (BootstrapSignals.has(BootstrapSignalKeys.LAUNCH_READY)) return;
        let cancelled = false;
        let stopPolling: (() => void) | undefined;
        void BootstrapSignals.whenAll([
            BootstrapSignalKeys.APP_INIT,
            BootstrapSignalKeys.START_INFO_UPDATE,
            BootstrapSignalKeys.CONSENT_UPDATED,
            BootstrapSignalKeys.USER_INFO_UPDATE,
            BootstrapSignalKeys.INFO_UPDATE,
        ]).then(() => {
            if (!cancelled) {
                void TrackerEngine.update({
                    type: LAUNCH_READY,
                    category: TrackingCategory.App.OPERATION,
                    detail: {},
                });
                stopPolling = signalPageViewToDataLayer();
            }
            // Always mark so TrackingProvider's PAGE_VIEW gate can unblock,
            // even when the waiter times out or this mount is cancelled.
            BootstrapSignals.mark(BootstrapSignalKeys.LAUNCH_READY);
        });
        return () => {
            cancelled = true;
            stopPolling?.();
        };
    }, []);
}
